import { c } from "react-compiler-runtime";
import { useGraphiQL, useGraphiQLActions } from "@graphiql/react";
import { parse, print, Kind, getNamedType, valueFromASTUntyped, parseValue } from "graphql";
import { useState, useRef, useEffect } from "react";
import { findOperation, definitionSelectionOffset, findDefinition } from "../lib/ast-path.js";
import { readVariables, resolveSchemaArg, resolveSchemaArgFromRoot, extractRawArgValue } from "../lib/schema-walk.js";
import { useCursorContext } from "./use-cursor-path.js";
import { toggleFieldSelection } from "../lib/field-selection.js";
import { argValueToValueNode } from "../lib/arg-value.js";
import { setFieldArgument } from "../lib/arguments.js";
import { suggestVarName, promoteArgToVariable, demoteVariable } from "../lib/variables.js";
import { addInlineFragment, removeInlineFragment } from "../lib/inline-fragments.js";
import { removeFragmentSpread, listFragments, createFragmentFromSelection, renameFragment, inlineFragment } from "../lib/fragments.js";
function tryParse(text) {
  const src = text == null ? void 0 : text.trim();
  if (!src) {
    return null;
  }
  try {
    return parse(src);
  } catch {
    return null;
  }
}
function emptyDoc() {
  return parse("{ __typename }");
}
function useWorkingDocument() {
  var _a, _b, _c, _d, _e;
  const $ = c(105);
  const schema = useGraphiQL(_temp);
  const queryEditor = useGraphiQL(_temp2);
  const variableEditor = useGraphiQL(_temp3);
  const operationName = useGraphiQL(_temp4);
  const {
    updateActiveTabValues
  } = useGraphiQLActions();
  const activeTabIndex = useGraphiQL(_temp5);
  const tabs = useGraphiQL(_temp6);
  const queryText = (_a = tabs[activeTabIndex]) == null ? void 0 : _a.query;
  const variablesText = (_b = tabs[activeTabIndex]) == null ? void 0 : _b.variables;
  let t0;
  if ($[0] !== queryText) {
    t0 = () => tryParse(queryText) ?? emptyDoc();
    $[0] = queryText;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [workingDoc, setWorkingDoc] = useState(t0);
  const lastSyncedRef = useRef(queryText ?? null);
  let t1;
  if ($[2] !== queryEditor || $[3] !== updateActiveTabValues) {
    t1 = (printed) => {
      if (queryEditor) {
        const position = queryEditor.getPosition();
        queryEditor.setValue(printed);
        if (position) {
          queryEditor.setPosition(position);
        }
        lastSyncedRef.current = queryEditor.getValue();
        return;
      }
      lastSyncedRef.current = printed;
      updateActiveTabValues({
        query: printed
      });
    };
    $[2] = queryEditor;
    $[3] = updateActiveTabValues;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const writeToEditor = t1;
  let t2;
  let t3;
  if ($[5] !== queryText) {
    t2 = () => {
      if (queryText === lastSyncedRef.current) {
        return;
      }
      lastSyncedRef.current = queryText ?? null;
      const parsed = tryParse(queryText);
      if (parsed) {
        setWorkingDoc(parsed);
      } else {
        if (!(queryText == null ? void 0 : queryText.trim())) {
          setWorkingDoc(emptyDoc());
        }
      }
    };
    t3 = [queryText];
    $[5] = queryText;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== operationName || $[9] !== workingDoc) {
    t4 = (_c = findOperation(workingDoc, operationName)) == null ? void 0 : _c.operation;
    $[8] = operationName;
    $[9] = workingDoc;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const activeOpKind = t4;
  const cursor = useCursorContext();
  const [activeFragment, setActiveFragment] = useState(null);
  const cursorFragment = ((_d = cursor.target) == null ? void 0 : _d.kind) === "fragment" ? cursor.target.name : ((_e = cursor.target) == null ? void 0 : _e.kind) === "operation" ? null : void 0;
  let t5;
  let t6;
  if ($[11] !== cursorFragment) {
    t5 = () => {
      if (cursorFragment !== void 0) {
        setActiveFragment(cursorFragment);
      }
    };
    t6 = [cursorFragment];
    $[11] = cursorFragment;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  if ($[14] !== activeFragment || $[15] !== operationName) {
    t7 = activeFragment != null ? {
      kind: "fragment",
      name: activeFragment
    } : {
      kind: "operation",
      name: operationName
    };
    $[14] = activeFragment;
    $[15] = operationName;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const target = t7;
  const cursorInTarget = cursor.target != null && (activeFragment != null ? cursor.target.kind === "fragment" && cursor.target.name === activeFragment : cursor.target.kind === "operation");
  const cursorPath = cursorInTarget ? cursor.path : void 0;
  let t8;
  if ($[17] !== queryEditor) {
    t8 = function moveCursorToTarget2(next) {
      setActiveFragment(next.kind === "fragment" ? next.name : null);
      if (!queryEditor) {
        return;
      }
      const model = queryEditor.getModel();
      if (!model) {
        return;
      }
      let offset;
      try {
        offset = definitionSelectionOffset(parse(model.getValue()), next);
      } catch {
        return;
      }
      if (offset != null) {
        queryEditor.setPosition(model.getPositionAt(offset));
      }
    };
    $[17] = queryEditor;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const moveCursorToTarget = t8;
  let t9;
  if ($[19] !== moveCursorToTarget) {
    t9 = function handleFocusFragment2(fragmentName) {
      moveCursorToTarget({
        kind: "fragment",
        name: fragmentName
      });
    };
    $[19] = moveCursorToTarget;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const handleFocusFragment = t9;
  let t10;
  if ($[21] !== moveCursorToTarget || $[22] !== operationName) {
    t10 = function handleBackToQuery2() {
      moveCursorToTarget({
        kind: "operation",
        name: operationName
      });
    };
    $[21] = moveCursorToTarget;
    $[22] = operationName;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const handleBackToQuery = t10;
  let t11;
  if ($[24] !== writeToEditor) {
    t11 = function applyDoc2(next_0) {
      setWorkingDoc(next_0);
      writeToEditor(print(next_0));
    };
    $[24] = writeToEditor;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const applyDoc = t11;
  let t12;
  if ($[26] !== updateActiveTabValues || $[27] !== variableEditor) {
    t12 = function applyVariables2(next_1) {
      const serialized = Object.keys(next_1).length ? JSON.stringify(next_1, null, 2) : "";
      if (variableEditor) {
        variableEditor.setValue(serialized);
      } else {
        updateActiveTabValues({
          variables: serialized
        });
      }
    };
    $[26] = updateActiveTabValues;
    $[27] = variableEditor;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const applyVariables = t12;
  let t13;
  if ($[29] !== applyVariables || $[30] !== variablesText) {
    t13 = function reconcileVariablesJson2(next_2) {
      const definedVars = /* @__PURE__ */ new Set();
      for (const def of next_2.definitions) {
        if (def.kind === Kind.OPERATION_DEFINITION) {
          for (const vd of def.variableDefinitions ?? []) {
            definedVars.add(vd.variable.name.value);
          }
        }
      }
      const vars = readVariables(variablesText);
      if (Object.keys(vars).some((name_0) => !definedVars.has(name_0))) {
        const reconciled = {};
        for (const [name, value] of Object.entries(vars)) {
          if (definedVars.has(name)) {
            reconciled[name] = value;
          }
        }
        applyVariables(reconciled);
      }
    };
    $[29] = applyVariables;
    $[30] = variablesText;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const reconcileVariablesJson = t13;
  let t14;
  if ($[32] !== applyDoc || $[33] !== reconcileVariablesJson || $[34] !== target || $[35] !== workingDoc) {
    t14 = function handleToggle2(path) {
      const next_3 = toggleFieldSelection(workingDoc, path, target);
      applyDoc(next_3);
      reconcileVariablesJson(next_3);
    };
    $[32] = applyDoc;
    $[33] = reconcileVariablesJson;
    $[34] = target;
    $[35] = workingDoc;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const handleToggle = t14;
  let t15;
  if ($[37] !== activeOpKind || $[38] !== schema || $[39] !== target || $[40] !== workingDoc) {
    t15 = function schemaArgFor2(path_0, argName) {
      if (!schema) {
        return;
      }
      if (target.kind === "operation") {
        return resolveSchemaArg(schema, activeOpKind, path_0, argName);
      }
      const def_0 = findDefinition(workingDoc, target);
      const rootTypeName = (def_0 == null ? void 0 : def_0.kind) === Kind.FRAGMENT_DEFINITION ? def_0.typeCondition.name.value : void 0;
      const rootType = rootTypeName ? schema.getType(rootTypeName) ?? void 0 : void 0;
      return resolveSchemaArgFromRoot(schema, rootType, path_0, argName);
    };
    $[37] = activeOpKind;
    $[38] = schema;
    $[39] = target;
    $[40] = workingDoc;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  const schemaArgFor = t15;
  let t16;
  if ($[42] !== applyDoc || $[43] !== schemaArgFor || $[44] !== target || $[45] !== workingDoc) {
    t16 = function handleSetArg2(path_1, argName_0, value_0) {
      const schemaArg = schemaArgFor(path_1, argName_0);
      if (!schemaArg) {
        return;
      }
      const valueNode = argValueToValueNode(schemaArg.type, value_0);
      applyDoc(setFieldArgument(workingDoc, path_1, argName_0, valueNode, target));
    };
    $[42] = applyDoc;
    $[43] = schemaArgFor;
    $[44] = target;
    $[45] = workingDoc;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const handleSetArg = t16;
  let t17;
  if ($[47] !== applyDoc || $[48] !== applyVariables || $[49] !== operationName || $[50] !== schemaArgFor || $[51] !== variablesText || $[52] !== workingDoc) {
    t17 = function handlePromoteArg2(path_2, argName_1, suggestedName) {
      const schemaArg_0 = schemaArgFor(path_2, argName_1);
      if (!schemaArg_0) {
        return;
      }
      const namedType = getNamedType(schemaArg_0.type);
      if (!namedType) {
        return;
      }
      const varName = suggestVarName(workingDoc, suggestedName);
      const raw = extractRawArgValue(workingDoc, path_2, argName_1, operationName);
      applyDoc(promoteArgToVariable(workingDoc, path_2, argName_1, varName, namedType.name, "", operationName));
      if (raw) {
        try {
          const jsValue = valueFromASTUntyped(parseValue(raw));
          applyVariables({
            ...readVariables(variablesText),
            [varName]: jsValue
          });
        } catch {
        }
      }
    };
    $[47] = applyDoc;
    $[48] = applyVariables;
    $[49] = operationName;
    $[50] = schemaArgFor;
    $[51] = variablesText;
    $[52] = workingDoc;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  const handlePromoteArg = t17;
  let t18;
  if ($[54] !== applyDoc || $[55] !== applyVariables || $[56] !== operationName || $[57] !== schemaArgFor || $[58] !== variablesText || $[59] !== workingDoc) {
    t18 = function handleDemoteArg2(path_3, argName_2, varName_0) {
      const vars_0 = readVariables(variablesText);
      const jsValue_0 = vars_0[varName_0];
      const schemaArg_1 = schemaArgFor(path_3, argName_2);
      let inlineValue;
      if (schemaArg_1 && jsValue_0 !== void 0) {
        inlineValue = argValueToValueNode(schemaArg_1.type, String(jsValue_0));
      }
      applyDoc(demoteVariable(workingDoc, varName_0, operationName, inlineValue));
      const rest = {
        ...vars_0
      };
      delete rest[varName_0];
      applyVariables(rest);
    };
    $[54] = applyDoc;
    $[55] = applyVariables;
    $[56] = operationName;
    $[57] = schemaArgFor;
    $[58] = variablesText;
    $[59] = workingDoc;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  const handleDemoteArg = t18;
  let t19;
  if ($[61] !== applyDoc || $[62] !== target || $[63] !== workingDoc) {
    t19 = function handleAddInlineFragment2(path_4, typeName) {
      applyDoc(addInlineFragment(workingDoc, path_4, typeName, target));
    };
    $[61] = applyDoc;
    $[62] = target;
    $[63] = workingDoc;
    $[64] = t19;
  } else {
    t19 = $[64];
  }
  const handleAddInlineFragment = t19;
  let t20;
  if ($[65] !== applyDoc || $[66] !== reconcileVariablesJson || $[67] !== target || $[68] !== workingDoc) {
    t20 = function handleRemoveInlineFragment2(path_5, typeName_0) {
      const next_4 = removeInlineFragment(workingDoc, path_5, typeName_0, target);
      applyDoc(next_4);
      reconcileVariablesJson(next_4);
    };
    $[65] = applyDoc;
    $[66] = reconcileVariablesJson;
    $[67] = target;
    $[68] = workingDoc;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  const handleRemoveInlineFragment = t20;
  let t21;
  if ($[70] !== applyDoc || $[71] !== reconcileVariablesJson || $[72] !== target || $[73] !== workingDoc) {
    t21 = function handleRemoveFragmentSpread2(path_6, fragmentName_0) {
      const next_5 = removeFragmentSpread(workingDoc, path_6, fragmentName_0, target);
      applyDoc(next_5);
      reconcileVariablesJson(next_5);
    };
    $[70] = applyDoc;
    $[71] = reconcileVariablesJson;
    $[72] = target;
    $[73] = workingDoc;
    $[74] = t21;
  } else {
    t21 = $[74];
  }
  const handleRemoveFragmentSpread = t21;
  let t22;
  if ($[75] !== applyDoc || $[76] !== target || $[77] !== workingDoc) {
    t22 = function handleExtractFragment2(path_7, typeName_1) {
      const base = `${typeName_1}Fields`;
      const existing = new Set(listFragments(workingDoc));
      let name_1 = base;
      for (let n = 2; existing.has(name_1); n++) {
        name_1 = `${base}_${n}`;
      }
      applyDoc(createFragmentFromSelection(workingDoc, path_7, name_1, typeName_1, target));
    };
    $[75] = applyDoc;
    $[76] = target;
    $[77] = workingDoc;
    $[78] = t22;
  } else {
    t22 = $[78];
  }
  const handleExtractFragment = t22;
  let t23;
  if ($[79] !== applyDoc || $[80] !== workingDoc) {
    t23 = function handleRenameFragment2(oldName, newName) {
      applyDoc(renameFragment(workingDoc, oldName, newName));
    };
    $[79] = applyDoc;
    $[80] = workingDoc;
    $[81] = t23;
  } else {
    t23 = $[81];
  }
  const handleRenameFragment = t23;
  let t24;
  if ($[82] !== activeFragment || $[83] !== applyDoc || $[84] !== moveCursorToTarget || $[85] !== operationName || $[86] !== workingDoc) {
    t24 = function handleDeleteFragment2(fragmentName_1) {
      applyDoc(inlineFragment(workingDoc, fragmentName_1));
      if (activeFragment === fragmentName_1) {
        moveCursorToTarget({
          kind: "operation",
          name: operationName
        });
      }
    };
    $[82] = activeFragment;
    $[83] = applyDoc;
    $[84] = moveCursorToTarget;
    $[85] = operationName;
    $[86] = workingDoc;
    $[87] = t24;
  } else {
    t24 = $[87];
  }
  const handleDeleteFragment = t24;
  let t25;
  if ($[88] !== activeOpKind || $[89] !== cursorPath || $[90] !== handleAddInlineFragment || $[91] !== handleBackToQuery || $[92] !== handleDeleteFragment || $[93] !== handleDemoteArg || $[94] !== handleExtractFragment || $[95] !== handleFocusFragment || $[96] !== handlePromoteArg || $[97] !== handleRemoveFragmentSpread || $[98] !== handleRemoveInlineFragment || $[99] !== handleRenameFragment || $[100] !== handleSetArg || $[101] !== handleToggle || $[102] !== target || $[103] !== workingDoc) {
    t25 = {
      workingDoc,
      activeOpKind,
      target,
      cursorPath,
      handleFocusFragment,
      handleBackToQuery,
      handleToggle,
      handleSetArg,
      handlePromoteArg,
      handleDemoteArg,
      handleAddInlineFragment,
      handleRemoveInlineFragment,
      handleRemoveFragmentSpread,
      handleExtractFragment,
      handleRenameFragment,
      handleDeleteFragment
    };
    $[88] = activeOpKind;
    $[89] = cursorPath;
    $[90] = handleAddInlineFragment;
    $[91] = handleBackToQuery;
    $[92] = handleDeleteFragment;
    $[93] = handleDemoteArg;
    $[94] = handleExtractFragment;
    $[95] = handleFocusFragment;
    $[96] = handlePromoteArg;
    $[97] = handleRemoveFragmentSpread;
    $[98] = handleRemoveInlineFragment;
    $[99] = handleRenameFragment;
    $[100] = handleSetArg;
    $[101] = handleToggle;
    $[102] = target;
    $[103] = workingDoc;
    $[104] = t25;
  } else {
    t25 = $[104];
  }
  return t25;
}
function _temp6(state_4) {
  return state_4.tabs;
}
function _temp5(state_3) {
  return state_3.activeTabIndex;
}
function _temp4(state_2) {
  return state_2.operationName;
}
function _temp3(state_1) {
  return state_1.variableEditor;
}
function _temp2(state_0) {
  return state_0.queryEditor;
}
function _temp(state) {
  return state.schema;
}
export {
  useWorkingDocument
};
//# sourceMappingURL=use-working-document.js.map
